import { Link, useSearch } from '@tanstack/react-router';
import { routes } from '../lib/routes';
import Icon from './Icon';
import HavingTrouble from './HavingTrouble';
import { cn } from '../lib/utils';
import { MenuIcon, X } from 'lucide-react';
import { useState } from 'react';
import { useRefundDetails } from '../hooks/useRefundDetails';
import { letterFlowSteps } from '../lib/constants';
import FacilityLogo from './FacilityLogo';
import { LanguagePicker } from './ui/language-picker';
import { EntityTypePicker } from './ui/entity-type-picker';
import { useTranslation } from 'react-i18next';

const Sidebar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const search = useSearch({ strict: false });

  const { data } = useRefundDetails();

  return (
    <aside className='flex flex-col md:h-full md:max-w-[320px]'>
      <div className='flex h-[100px] flex-col px-6 py-4'>
        <div className='flex h-full justify-between gap-1 md:mb-16'>
          <FacilityLogo
            alignment='center'
            facility_logo={data?.refundData.facility_logo}
            business_unit_logo={data?.refundData.business_unit_logo}
          />
          <div
            className='flex cursor-pointer items-center justify-center md:hidden'
            onClick={() => setIsOpen(!isOpen)}
          >
            {!isOpen ? <MenuIcon size={24} /> : <X size={24} />}
          </div>
        </div>
      </div>

      <div
        className={cn(
          'absolute z-10 mt-[82px] flex h-[calc(100vh-82px)] w-full -translate-x-full flex-col justify-between bg-card p-6 transition-transform duration-200 ease-in-out md:relative md:mt-0 md:translate-x-0',
          !isOpen ? '-translate-x-full' : 'translate-x-0',
        )}
      >
        <div className='w-full flex-col gap-2 md:visible md:flex'>
          <div className='flex flex-wrap gap-2 md:flex md:px-4 md:pb-2 lg:hidden'>
            <EntityTypePicker />
            <LanguagePicker />
          </div>
          {routes.map((route) => (
            <Link
              key={route.id}
              to={route.to}
              search={(prev) => {
                return {
                  ...prev,
                  step: 1,
                  paymentType: undefined,
                };
              }}
              className='flex items-center justify-between rounded-md p-3 pl-4 transition-colors hover:bg-secondary hover:font-bold'
              target={route.target}
            >
              <span className='flex items-center gap-2 text-secondary-foreground'>
                <Icon name={route.icon} size={24} />
                {t(route.title)}
              </span>
              {route.secondIcon && <Icon name={route.secondIcon} size={24} />}
            </Link>
          ))}
        </div>

        <div className='flex flex-col gap-2'>
          {letterFlowSteps.map((route, index) => (
            <div
              key={index}
              className='flex cursor-default flex-row items-center gap-2 rounded-md p-3 pb-6 [&.active]:bg-secondary [&.active]:font-bold'
            >
              <div className='relative'>
                {index + 1 > (search.step ?? 0) ? (
                  <span
                    className={cn(
                      'flex items-center justify-center rounded-full border-2 px-1.5 py-0.5 text-xs',
                      search.step === index
                        ? 'border-primary-600 font-semibold text-primary-600'
                        : 'border-gray-400',
                    )}
                  >
                    {index + 1}
                  </span>
                ) : (
                  <div className='size-6'>
                    <img
                      src='/checkbox.svg'
                      alt={route.title}
                      className='size-full'
                    />
                  </div>
                )}
                {index !== letterFlowSteps.length - 1 && (
                  <span className='absolute top-10 left-1/2 h-12 w-px -translate-x-1/2 bg-gray-300' />
                )}
              </div>
              <div className='flex min-h-16 flex-col justify-center'>
                <span
                  className={cn(
                    'flex items-center gap-2 text-sm font-bold',
                    search.step === index
                      ? 'text-primary-700'
                      : 'text-gray-400',
                  )}
                >
                  {t(route.title)}
                </span>
              </div>
            </div>
          ))}
        </div>

        <HavingTrouble />
      </div>
    </aside>
  );
};

export default Sidebar;
